import { morph } from '@theme/morph';
import { CartAddEvent } from '@theme/events';

/**
 * Combo Discount card behavior.
 *  - Click on the card (outside the button) → open the Quick Add modal for the partner product.
 *  - Click on the "Add both" button → POST both host and partner variants to /cart/add.js, then dispatch
 *    CartAddEvent so Horizon's <cart-drawer-component auto-open> opens automatically.
 *
 * Delegated on document so this runs once globally and survives view transitions.
 */

if (!window.__tpaComboDiscountLoaded) {
  window.__tpaComboDiscountLoaded = true;

  const addBothInFlight = new WeakSet();

  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('change', handleVariantSelectChange);
  document.addEventListener('variant:update', handleVariantUpdate);
}

async function handleClick(event) {
  // Let native select clicks through without triggering the quick-add modal.
  if (event.target.closest('[data-combo-variant-select]')) return;

  const btn = event.target.closest('[data-combo-add]');
  if (btn) {
    event.preventDefault();
    event.stopPropagation();
    const card = btn.closest('.tpa-combo__card');
    if (card) await addBothToCart(card, btn);
    return;
  }

  const card = event.target.closest('.tpa-combo__card');
  if (card) {
    event.preventDefault();
    await openPartnerQuickAdd(card);
  }
}

function handleVariantSelectChange(event) {
  const sel = event.target.closest('[data-combo-variant-select]');
  if (!sel) return;
  const card = sel.closest('.tpa-combo__card');
  if (!card) return;
  const opt = sel.selectedOptions[0];
  if (!opt) return;

  card.dataset.partnerVariantId = opt.value;

  const img = card.querySelector('.tpa-combo__image');
  if (img && opt.dataset.image) {
    img.src = opt.dataset.image;
    img.removeAttribute('srcset');
  }

  const orig = card.querySelector('.tpa-combo__price--original');
  if (orig && opt.dataset.originalPrice) orig.textContent = opt.dataset.originalPrice;

  const sale = card.querySelector('.tpa-combo__price--sale');
  if (sale && opt.dataset.salePrice) sale.textContent = opt.dataset.salePrice;
}

function handleKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const card = event.target.closest('.tpa-combo__card');
  if (!card || event.target.closest('[data-combo-add]')) return;
  event.preventDefault();
  openPartnerQuickAdd(card);
}

function handleVariantUpdate(event) {
  const detail = event?.detail ?? {};
  const resource = detail.resource ?? {};
  const newId =
    resource.variant?.id ??
    resource.id ??
    detail.variant?.id ??
    detail.data?.variantId;
  if (!newId) return;
  document.querySelectorAll('.tpa-combo__card').forEach((card) => {
    card.dataset.hostVariantId = String(newId);
  });
}

async function addBothToCart(card, btn) {
  const hostVariantId = parseInt(card.dataset.hostVariantId, 10);
  const partnerVariantId = parseInt(card.dataset.partnerVariantId, 10);
  if (!hostVariantId || !partnerVariantId) return;

  btn.disabled = true;
  const originalLabel = btn.textContent;

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
      body: JSON.stringify({
        items: [
          { id: hostVariantId, quantity: 1 },
          { id: partnerVariantId, quantity: 1 },
        ],
      }),
    });

    if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

    document.dispatchEvent(
      new CartAddEvent({}, String(partnerVariantId), {
        source: 'combo-discount',
        productId: String(partnerVariantId),
      })
    );
  } catch (err) {
    console.error('[combo-discount] add both failed', err);
    btn.textContent = originalLabel;
  } finally {
    btn.disabled = false;
  }
}

async function openPartnerQuickAdd(card) {
  const dialog = document.getElementById('quick-add-dialog');
  const modalContent = document.getElementById('quick-add-modal-content');
  const partnerUrl = card.dataset.partnerUrl;

  if (!dialog || !modalContent || !partnerUrl || typeof dialog.showDialog !== 'function') {
    if (partnerUrl) window.location.assign(partnerUrl);
    return;
  }

  try {
    const response = await fetch(partnerUrl);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = html.querySelector('[data-product-grid-content]');
    if (!grid) throw new Error('No product grid content in partner page');

    morph(modalContent, grid.cloneNode(true));
    hydratePvgGallery(modalContent);
    applyPackSwatchesInRoot(modalContent);
    injectViewProductLink(modalContent, partnerUrl);
    containDialogEvents(dialog);
    dialog.showDialog();
  } catch (err) {
    console.error('[combo-discount] quick-add failed', err);
    window.location.assign(partnerUrl);
  }
}

/**
 * The custom pvg gallery is populated by an inline <script> on the original product page.
 * Inline scripts don't execute when HTML is inserted via morph, so the stage stays empty.
 * Read the JSON data island, render the first variant's images into the stage + thumbs.
 */
function hydratePvgGallery(root) {
  const dataEl = root.querySelector('script[id^="pvg-data-"]');
  if (!dataEl) return;
  const pid = dataEl.id.replace('pvg-data-', '');
  const stage = root.querySelector('#pvg-stage-' + pid);
  const thumbs = root.querySelector('#pvg-thumbs-' + pid);
  if (!stage) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const keys = Object.keys(data);
  if (!keys.length) return;
  const images = data[keys[0]];
  if (!images || !images.length) return;

  stage.innerHTML = images
    .map(
      (img, i) =>
        `<img src="${img.src}" alt="${img.alt || ''}" loading="${i === 0 ? 'eager' : 'lazy'}">`
    )
    .join('');

  if (thumbs && images.length > 1) {
    thumbs.innerHTML = images
      .map(
        (img, i) =>
          `<button class="pvg__thumb${i === 0 ? ' pvg__thumb--active' : ''}" data-index="${i}" aria-label="Image ${i + 1}"><img src="${img.thumb}" alt="${img.alt || ''}" loading="lazy"></button>`
      )
      .join('');
    thumbs.querySelectorAll('.pvg__thumb').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.index, 10);
        stage.scrollTo({ left: idx * stage.offsetWidth, behavior: 'smooth' });
        thumbs.querySelectorAll('.pvg__thumb').forEach((t, i) => {
          t.classList.toggle('pvg__thumb--active', i === idx);
        });
      });
    });
  }
}

/**
 * Apply pack-swatch styling to variant buttons inside the modal.
 * The IIFE in variant-main-picker.liquid only runs on DOMContentLoaded / variant:update;
 * morphed-in buttons would otherwise render as plain CTAs until the first variant change.
 */
function applyPackSwatchesInRoot(root) {
  root.querySelectorAll('[data-pack-swatch-src]').forEach((label) => {
    if (label.classList.contains('variant-option__button-label--pack-swatch')) return;
    const src = label.dataset.packSwatchSrc;
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      label.classList.add('variant-option__button-label--pack-swatch');
      label.style.setProperty('--pack-swatch-url', `url('${src}')`);
    };
    img.src = src;
  });
}

/**
 * Dialog-scoped handlers:
 *  1. Re-apply pack-swatch styling when Horizon re-renders the variant buttons after a change.
 *  2. Re-render the pvg gallery for the newly selected variant (the inline script never runs here).
 *  3. Stop variant:update/selected/change events from bubbling to document — otherwise the host
 *     product on the page behind the modal tries to switch to a variant that doesn't exist.
 * Attached once per dialog element via a marker flag.
 */
function containDialogEvents(dialog) {
  if (dialog.__tpaEventsContained) return;
  dialog.__tpaEventsContained = true;

  const onVariantUpdate = (e) => {
    applyPackSwatchesInRoot(dialog);
    const variantId =
      e?.detail?.resource?.variant?.id ??
      e?.detail?.resource?.id ??
      e?.detail?.variant?.id;
    if (variantId) renderPvgForVariant(dialog, String(variantId));
  };

  dialog.addEventListener('variant:update', onVariantUpdate);
  ['variant:update', 'variant:selected', 'variant:change'].forEach((name) => {
    dialog.addEventListener(name, (e) => e.stopPropagation());
  });
}

/**
 * Render the pvg gallery inside the given root to show images for a specific variant.
 */
function renderPvgForVariant(root, variantId) {
  const dataEl = root.querySelector('script[id^="pvg-data-"]');
  if (!dataEl) return;
  const pid = dataEl.id.replace('pvg-data-', '');
  const stage = root.querySelector('#pvg-stage-' + pid);
  const thumbs = root.querySelector('#pvg-thumbs-' + pid);
  if (!stage) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const images = data[variantId];
  if (!images || !images.length) return;

  stage.innerHTML = images
    .map(
      (img, i) =>
        `<img src="${img.src}" alt="${img.alt || ''}" loading="${i === 0 ? 'eager' : 'lazy'}">`
    )
    .join('');
  stage.scrollLeft = 0;

  if (thumbs) {
    thumbs.innerHTML = images.length > 1
      ? images
          .map(
            (img, i) =>
              `<button class="pvg__thumb${i === 0 ? ' pvg__thumb--active' : ''}" data-index="${i}" aria-label="Image ${i + 1}"><img src="${img.thumb}" alt="${img.alt || ''}" loading="lazy"></button>`
          )
          .join('')
      : '';
    thumbs.querySelectorAll('.pvg__thumb').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.index, 10);
        stage.scrollTo({ left: idx * stage.offsetWidth, behavior: 'smooth' });
        thumbs.querySelectorAll('.pvg__thumb').forEach((t, i) => {
          t.classList.toggle('pvg__thumb--active', i === idx);
        });
      });
    });
  }
}

/**
 * Add a "View full product" link inside the modal that navigates to the partner PDP.
 */
function injectViewProductLink(root, partnerUrl) {
  if (!partnerUrl) return;
  if (root.querySelector('[data-combo-view-product]')) return;

  const link = document.createElement('a');
  link.href = partnerUrl;
  link.className = 'tpa-combo__view-product button button--secondary';
  link.dataset.comboViewProduct = '';
  link.textContent = 'View full product';

  const productForm = root.querySelector('product-form-component');
  if (productForm?.parentElement) {
    productForm.parentElement.insertBefore(link, productForm);
    return;
  }

  const details = root.querySelector('.product-details');
  (details || root).prepend(link);
}
